import * as React from 'react'
import TimesheetTableRow, { TimesheetViewMode } from './TimesheetTableRow'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { useTable } from '@/modules/drivers/context/TableContext'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

interface TimesheetTableProps {
  data: any[]
  viewMode: TimesheetViewMode
  showBonusFields?: boolean
  onDriverClick?: (driverId: string) => void
  companyId: string
  loading?: boolean
  selectedYear?: number
}

const TimesheetTable: React.FC<TimesheetTableProps> = ({
  data,
  viewMode,
  showBonusFields = false,
  onDriverClick,
  companyId,
  loading = false,
  selectedYear,
}) => {
  const { toggleAll, toggleId, selectedGroup, isSelectedAny, setAllIds } =
    useTable()
  const { t } = useLocalize()

  const columns: TableHeaderColumn[] = React.useMemo(() => {
    if (viewMode === 'bonus-reward') {
      const baseColumns: TableHeaderColumn[] = [
        {
          title: t('timesheet.col-driver-name'),
          key: 'driverName',
          type: 'string',
          sort: true,
        },
        {
          title: t('timesheet.col-vacation-nb'),
          key: 'vacationNb',
          type: 'number',
          sort: true,
        },
        {
          title: t('timesheet.col-sick-leave-nb'),
          key: 'sickLeaveNb',
          type: 'number',
          sort: true,
        },
        {
          title: t('timesheet.col-costs'),
          key: 'costs',
          type: 'number',
          sort: true,
        },
        {
          title: t('timesheet.col-domestic-delegations'),
          key: 'domesticDelegations',
          type: 'number',
          sort: true,
        },
        {
          title: t('timesheet.col-expected-netto'),
          key: 'expectedNetto',
          type: 'number',
          sort: true,
        },
        {
          title: t('timesheet.col-calculated-netto'),
          key: 'calculatedNetto',
          type: 'number',
          sort: true,
        },
      ]

      if (showBonusFields) {
        baseColumns.push(
          {
            title: t('timesheet.col-reward'),
            key: 'reward',
            type: 'number',
            sort: true,
          },
          {
            title: t('timesheet.col-gross-total'),
            key: 'grossTotal7Day',
            type: 'number',
            sort: true,
          },
        )
      } else {
        baseColumns.push({
          title: t('timesheet.col-gross-total'),
          key: 'grossTotal7Day',
          type: 'number',
          sort: true,
        })
      }

      return baseColumns
    } else {
      // Group view (widok grupowy)
      return [
        {
          title: t('timesheet.col-driver-name'),
          key: 'driverName',
          type: 'string',
          sort: true,
        },
        {
          title: t('timesheet.col-calculation-status'),
          key: 'calculations',
          type: 'number',
          sort: true,
        },
        {
          title: t('timesheet.col-costs'),
          key: 'costs',
          type: 'number',
          sort: true,
        },
        {
          title: t('timesheet.col-gross-total'),
          key: 'grossTotal',
          type: 'number',
          sort: true,
        },
        {
          title: t('timesheet.col-last-calculation-date'),
          key: 'lastCalculationDate',
          type: 'date',
          sort: true,
        },
      ]
    }
    // `t` must stay in the deps - it changes identity when the language changes,
    // and without it the headers would stay frozen in the previous language.
  }, [viewMode, showBonusFields, t])

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data,
    tableColumns: columns,
  })

  const BATCH_SIZE = 30
  const [visibleCount, setVisibleCount] = React.useState(BATCH_SIZE)
  const scrollRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setVisibleCount(BATCH_SIZE)
  }, [sortConfig, data])

  const visibleRows = React.useMemo(
    () => sortedData.slice(0, visibleCount),
    [sortedData, visibleCount],
  )

  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    if (visibleCount >= sortedData.length) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, sortedData.length))
    }
  }, [visibleCount, sortedData.length])

  React.useEffect(() => {
    setAllIds(
      data.map((item) => ({
        id: item.id,
        isActive: true,
      })),
    )
  }, [data, setAllIds])

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      {loading ? (
        <div className="tw-grid tw-w-full tw-flex-1 tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : (
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="tw-overflow-y-auto"
        >
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              maxWidthOff
              disabledCheckboxToggle
              columns={columns}
              onHeaderClick={handleHeaderClick}
              sortConfig={sortConfig}
              fontSize="sm"
              bolder={true}
              opacity={true}
              disabledToggle={false}
              sticky
            />
            <tbody>
              {visibleRows.map((row) => (
                <TimesheetTableRow
                  key={row.id}
                  data={row}
                  viewMode={viewMode}
                  showBonusFields={showBonusFields}
                  selectedIds={selectedGroup ?? null}
                  isSelectedAny={isSelectedAny}
                  companyId={companyId}
                  selectedYear={selectedYear}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export default TimesheetTable
